'use client'

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { SelectUser } from '@package/drizzle/schema'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  toast,
} from '@ui/web/components'
import { getStaticAssetUrl } from '@helper/utils/file'

import { useSharedTRPC } from '../context'

interface GivePremiumSeatDialogProps {
  user: Pick<SelectUser, 'id' | 'name'>
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function GivePremiumSeatDialog({ user, open, onOpenChange }: GivePremiumSeatDialogProps) {
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const trpc = useSharedTRPC()

  const queryClient = useQueryClient()

  const givePremiumSeatMutation = useMutation(
    trpc.myOrganization.giveChildPremium.mutationOptions({
      onSuccess: async () => {
        toast.success('Kursi premium berhasil diberikan')
        onOpenChange(false)
        await queryClient.invalidateQueries({
          queryKey: trpc.myOrganization.listChildren.queryKey(),
        })
        // Every owned-seat read the handout moves, matching what `ManageSeatsDialog` invalidates:
        // the seat ledger, the pool strips, and the summary the roster header and the seats alert
        // both count from. Invalidating one of the three leaves the others showing pre-handout
        // numbers on the same screen.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getActiveSeats.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatPools.queryKey() }),
          // Scheduled pools are a separate query key; a seat handed out from a future term lives
          // only there, and the header totals read it.
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatPoolsWithScheduled.queryKey() }),
          queryClient.invalidateQueries({ queryKey: trpc.myOrganization.getSeatSummary.queryKey() }),
        ])
      },
      onError: (error) => {
        toast.error('Ada error yang terjadi saat memberikan kursi premium')
        setErrorMsg(error.message)
      },
    }),
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='srd:max-w-sm' showCloseButton={false}>
        <div className='srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-8 srd:text-center'>
          <img
            src={getStaticAssetUrl('illustration/puti.png')}
            alt='Puti'
            width={70}
            height={70}
            draggable={false}
            className='srd:block'
          />

          <DialogHeader className='srd:flex srd:flex-col srd:items-center srd:gap-2 srd:text-center'>
            <DialogTitle className='srd:text-lg srd:font-bold'>Berikan {user.name} Kursi Premium?</DialogTitle>
            <DialogDescription className='srd:sr-only'>
              Konfirmasi memberikan kursi premium kepada {user.name}
            </DialogDescription>
          </DialogHeader>

          {errorMsg && <p className='srd:text-xs srd:text-main-red-100'>{errorMsg}</p>}

          <div className='srd:flex srd:w-full srd:flex-col srd:items-center srd:justify-center srd:gap-y-2'>
            <Button
              disabled={givePremiumSeatMutation.isPending}
              loading={givePremiumSeatMutation.isPending}
              className='srd:w-full srd:rounded-full srd:bg-main-blue-100 srd:font-medium srd:text-neutrals-white'
              onClick={() => givePremiumSeatMutation.mutate({ userId: user.id })}
            >
              Ya, berikan kursi premium
            </Button>

            <DialogClose
              render={
                <Button
                  variant='link'
                  className='srd:text-main-blue-100 srd:hover:no-underline'
                  disabled={givePremiumSeatMutation.isPending}
                >
                  Tidak jadi
                </Button>
              }
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
